import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Heart, ShieldCheck, Upload, MessageCircle, X, ChevronRight } from 'lucide-react';

const SEEN_KEY = 'x7autoz_onboarding_seen';

const SLIDES = [
  {
    icon: Search,
    title: 'Find your perfect ride',
    body: 'Search cars, bikes, EVs and commercial vehicles by brand, model, budget or registration number — right from the search bar up top.',
  },
  {
    icon: ShieldCheck,
    title: 'Buy with confidence',
    body: 'Look for the verified badge — those vehicles have been physically inspected by our partner network before listing.',
  },
  {
    icon: Upload,
    title: 'Sell in minutes',
    body: 'Tap "Sell" from the menu, add your vehicle details and photos, and your listing goes live after a quick review.',
  },
  {
    icon: Heart,
    title: 'Save your favorites',
    body: 'Tap the heart on any listing to save it to your Wishlist — come back to it anytime from the header.',
  },
  {
    icon: MessageCircle,
    title: 'Chat directly with sellers',
    body: 'Message sellers and buyers straight from a vehicle\u2019s page using the built-in chat — no need to share your number.',
  },
];

// Fires once, the first time the app is opened as an installed app (standalone
// display mode) — the moment a "first download" tutorial is expected to appear.
const OnboardingTour = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    const alreadySeen = localStorage.getItem(SEEN_KEY);
    if (isStandalone && !alreadySeen) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, '1');
    setVisible(false);
  };

  const next = () => {
    if (step < SLIDES.length - 1) {
      setStep(step + 1);
    } else {
      dismiss();
      navigate('/browse');
    }
  };

  if (!visible) return null;

  const { icon: Icon, title, body } = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden animate-slide-up">
        <div className="relative bg-black text-white px-6 pt-8 pb-10 text-center">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-white"
            aria-label="Skip tutorial"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
            <Icon className="h-7 w-7" />
          </div>
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>

        <div className="px-6 py-6 text-center space-y-6">
          <p className="text-sm text-gray-600 leading-relaxed">{body}</p>

          <div className="flex items-center justify-center gap-1.5">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-black' : 'w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={dismiss}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              Skip
            </button>
            <button
              onClick={next}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-black text-white hover:bg-gray-800 flex items-center justify-center gap-1"
            >
              {isLast ? 'Start Exploring' : 'Next'}
              {!isLast && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
